<script lang="ts" setup>
    import BaseButton from '@/components/BaseButton.vue';
    import BaseIcon from '@/components/BaseIcon.vue';
    import { useTemplateStore } from '@/store/template.store';
    import { GRADUATION_NAME } from '@/littleWedding/projectConstantes';
    import { useConfigStore } from '@/store/config.store';
    import IconUnlock from '@/components/icons/IconUnlock.vue';
    import IconLock from '@/components/icons/IconLock.vue';
    import { useContextMenuPositionning } from '@/composables/useContextMenuPositionning';
    import { CUTTING_GROUP_NAME, GRID_HELPER } from '@/littleWedding/fabricGroupsName';
    import { deleteSelectedProductImage } from '@/utils/deleteSelectedProductImage';
    import { dataStore } from '@/commons/DataStore';
    import { storeToRefs } from 'pinia';
    import { useTemplateRef, computed } from 'vue';
    import { useI18n } from 'vue-i18n';
    import type { IImageField } from '@/typescript/interfaces/IImageField';

    const { t } = useI18n();

    const templateStore = useTemplateStore();
    const { selectedField, selectedFieldId } = storeToRefs(templateStore);
    const { bringFieldBackward, bringFieldForward, bringFieldToBack, bringFieldToFront, removeField } = templateStore;
    const { currentSide, cutpathFieldList, isAdmin, productImages } = storeToRefs(useConfigStore());
    const contextMenu = useTemplateRef<HTMLDivElement>('contextMenu');
    const fabricWorldRecto = dataStore.wFabricWorldRecto;
    const fabricWorldVerso = dataStore.wFabricWorldVerso;
    const { absoluteLeft, absoluteTop, isLayerManagementVisible, layerMenuPosition } = useContextMenuPositionning(
        contextMenu,
        selectedField,
        fabricWorldRecto,
        fabricWorldVerso,
    );

    const isGridSelected = computed(() => selectedFieldId.value === GRID_HELPER);

    const selectedCutpath = computed(() => selectedFieldId.value === CUTTING_GROUP_NAME);

    const layerManagementOptions = [
        { label: t('move_to_top'), action: moveToTop },
        { label: t('move_forward'), action: moveForward },
        { label: t('move_backward'), action: moveBackward },
        { label: t('move_to_bottom'), action: moveToBottom },
    ];

    function removeFieldAction() {
        if (isGridSelected.value) {
            deleteGrid();
        }
        if (selectedCutpath.value) {
            deleteCutpath();
        }
        if (!selectedField.value) return;
        removeField(selectedField.value);
    }

    async function deleteGrid() {
        try {
            selectedFieldId.value = undefined;
            if (currentSide.value === 'recto') {
                fabricWorldRecto.deleteGridHelper();
            } else {
                fabricWorldVerso.deleteGridHelper();
            }
            await deleteSelectedProductImage(currentSide.value, 'helpGrid', productImages.value);
        } catch (error) {
            console.error('Error deleting grid image:', error);
            return;
        }
    }

    async function deleteCutpath() {
        try {
            selectedFieldId.value = undefined;
            cutpathFieldList.value = [];
            fabricWorldRecto.deleteCuttingPath();

            await deleteSelectedProductImage(currentSide.value, 'cutPath', productImages.value);
        } catch (error) {
            console.error('Error deleting grid image:', error);
            return;
        }
    }

    function moveForward() {
        if (!selectedField.value) return;
        bringFieldForward(selectedField.value);
        isLayerManagementVisible.value = false;
    }

    function moveBackward() {
        if (!selectedField.value) return;
        bringFieldBackward(selectedField.value);
        isLayerManagementVisible.value = false;
    }

    function moveToTop() {
        if (!selectedField.value) return;
        bringFieldToFront(selectedField.value);
        isLayerManagementVisible.value = false;
    }

    function moveToBottom() {
        if (!selectedField.value) return;
        bringFieldToBack(selectedField.value);
        isLayerManagementVisible.value = false;
    }

    const displayMenu = computed(() => {
        return (
            (!!selectedField.value && (selectedField.value as IImageField)?.filename !== GRADUATION_NAME) ||
            isGridSelected.value ||
            selectedCutpath.value
        );
    });

    function toggleSelectedFieldEdition() {
        if (!selectedField.value) {
            return;
        }
        selectedField.value.editable = !selectedField.value.editable;
    }
</script>

<template>
    <div
        v-show="displayMenu"
        ref="contextMenu"
        class="absolute z-10 flex flex-row gap-4 rounded-full bg-bg-secondary p-2 shadow-dropdown"
        :style="{ left: absoluteLeft, top: absoluteTop }"
    >
        <BaseButton
            v-if="!isGridSelected && !selectedCutpath"
            :variant="isLayerManagementVisible ? 'filled' : 'outlined'"
            class="h-9 w-9 rounded-full active:bg-black active:text-white"
            @click="isLayerManagementVisible = !isLayerManagementVisible"
        >
            <span class="sr-only">
                {{ $t('manage_layers') }}
            </span>
            <BaseIcon
                class="h-6 w-6 active:text-white"
                name="Square3Stack3DIcon"
                type="outlined"
            />
        </BaseButton>

        <BaseButton
            variant="outlined"
            class="h-9 w-9 rounded-full active:bg-black active:text-white"
            @click="removeFieldAction"
        >
            <span class="sr-only">
                {{ $t('delete_field') }}
            </span>
            <BaseIcon
                class="h-6 w-6 active:text-white"
                name="TrashIcon"
                type="outlined"
            />
        </BaseButton>
        <BaseButton
            v-if="isAdmin && !isGridSelected && !selectedCutpath"
            variant="outlined"
            class="h-9 w-9 rounded-full"
            @click="toggleSelectedFieldEdition"
        >
            <span class="sr-only">{{ $t('lock_field') }}</span>
            <IconUnlock
                v-if="selectedField && selectedField.editable"
                class="h-6 w-6"
            />
            <IconLock
                v-else
                class="h-6 w-6"
            />
        </BaseButton>
        <transition
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
            class="duration-300"
        >
            <div
                v-if="isLayerManagementVisible"
                :class="[
                    'absolute flex w-56 flex-col bg-white p-4 font-tw-cen shadow-dropdown',
                    layerMenuPosition.top && 'bottom-14',
                    !layerMenuPosition.top && 'top-14',
                    layerMenuPosition.left && '-left-1/2',
                    !layerMenuPosition.left && 'left-0',
                ]"
            >
                <button
                    v-for="option in layerManagementOptions"
                    :key="option.label"
                    class="w-full appearance-none py-1 hover:bg-hover-quaternary active:bg-black active:text-white"
                    @click="option.action"
                >
                    {{ option.label }}
                </button>
            </div>
        </transition>
    </div>
</template>
